import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { updatePreferenceWeights, type TestingFeedback } from '../domain/feedback/schema';
import type { Preferences } from '../domain/preferences';
import type { LocalProfile } from '../domain/profile';
import type { StyleProfile } from '../domain/style/schema';
import { persistence } from '../services/persistence';

interface AppContextValue {
  profile: LocalProfile;
  styleReferencePreviews: string[];
  setStyleReferencePreviews(previews: string[]): void;
  setStyleProfile(styleProfile: StyleProfile): void;
  setPreferences(preferences: Preferences): void;
  toggleSaved(id: string): void;
  markTested(id: string): void;
  submitFeedback(feedback: TestingFeedback): string[];
  setRefined(refined: boolean): void;
  deleteProfile(): void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<LocalProfile>(() => persistence.load());
  const [styleReferencePreviews, setReferencePreviews] = useState<string[]>([]);

  useEffect(() => {
    persistence.save(profile);
  }, [profile]);

  const value = useMemo<AppContextValue>(
    () => ({
      profile,
      styleReferencePreviews,
      setStyleReferencePreviews: (previews) =>
        setReferencePreviews((current) => {
          current.filter((url) => !previews.includes(url)).forEach((url) => URL.revokeObjectURL(url));
          return previews;
        }),
      setStyleProfile: (styleProfile) =>
        setProfile((current) => ({
          ...current,
          styleProfile,
          styleRegenerationRequired: false,
          migrationNotice: null,
          updatedAt: new Date().toISOString(),
        })),
      setPreferences: (preferences) =>
        setProfile((current) => ({ ...current, preferences, updatedAt: new Date().toISOString() })),
      toggleSaved: (id) =>
        setProfile((current) => ({
          ...current,
          savedIds: current.savedIds.includes(id)
            ? current.savedIds.filter((item) => item !== id)
            : [...current.savedIds, id],
        })),
      markTested: (id) =>
        setProfile((current) => ({
          ...current,
          testedIds: current.testedIds.includes(id) ? current.testedIds : [...current.testedIds, id],
        })),
      submitFeedback: (feedback) => {
        const update = updatePreferenceWeights(profile.feedbackWeights, feedback);
        setProfile((current) => ({
          ...current,
          feedback: [...current.feedback.filter((item) => item.fragranceId !== feedback.fragranceId), feedback],
          testedIds: current.testedIds.includes(feedback.fragranceId)
            ? current.testedIds
            : [...current.testedIds, feedback.fragranceId],
          feedbackWeights: update.weights,
          feedbackChanges: update.changes,
          refined: true,
        }));
        return update.changes;
      },
      setRefined: (refined) => setProfile((current) => ({ ...current, refined })),
      deleteProfile: () => {
        persistence.clear();
        setReferencePreviews((current) => {
          current.forEach((url) => URL.revokeObjectURL(url));
          return [];
        });
        setProfile({ ...persistence.load(), updatedAt: new Date().toISOString() });
      },
    }),
    [profile, styleReferencePreviews],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// This hook intentionally shares the context module with its provider.
// eslint-disable-next-line react-refresh/only-export-components
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used inside AppProvider');
  return context;
};
